import type { ActorDataExport } from "#actor-data-export-schema";
import {
  ACTOR_USER_ID,
  type ActorCardRowIds,
  FAVORITES_COLLECTION_ID,
  deriveCollectionMorphemeId,
  deriveGraphemeMeaningCardRowIds,
  deriveGraphemePronunciationCardRowIds,
  deriveMorphemeNoteId,
  deriveMorphemeSearchVisitId,
  deriveQuizCardRowIds,
  deriveUserSentenceId,
  deriveUserSlideId,
  deriveUserWordId,
  deriveWordSentenceCardRowIds,
} from "@-/dev.improve.haniverse.database.schemas.actor.identity";

type ExportTables = ActorDataExport["tables"];
type ExportUserSlide = ExportTables["userSlide"][number];

/** An export whose rows carry the ids this app gives them. A slide row always has its key. */
export type NormalizedActorDataTables = Omit<ExportTables, "userSlide"> & {
  userSlide: (Omit<ExportUserSlide, "key"> & { key: string })[];
};

type CrudRow = {
  createdAt: string;
  deletedAt: string | null;
  id: string;
  updatedAt: string | null;
};

/** A live row first, then the row that already has `preferredId`, then the latest written. */
function compareRows(preferredId: string) {
  return (a: CrudRow, b: CrudRow): number =>
    Number(a.deletedAt !== null) - Number(b.deletedAt !== null) ||
    Number(a.id !== preferredId) - Number(b.id !== preferredId) ||
    (b.updatedAt ?? b.createdAt).localeCompare(a.updatedAt ?? a.createdAt) ||
    a.id.localeCompare(b.id);
}

function groupRows<Row>(rows: readonly Row[], keyOf: (row: Row) => string): Map<string, Row[]> {
  const groups = new Map<string, Row[]>();
  for (const row of rows) {
    const key = keyOf(row);
    const group = groups.get(key);
    if (group === undefined) {
      groups.set(key, [row]);
    } else {
      group.push(row);
    }
  }
  return groups;
}

/**
 * Give every row of a derived-identity table the id derived from its key. Rows that share a key
 * are one row to this app: the winner (see {@link compareRows}) is kept and passed to `merge`
 * with the others, in case they hold something the winner lacks.
 */
function rekeyRows<Row extends CrudRow>(
  rows: readonly Row[],
  deriveId: (row: Row) => string,
  merge: (winner: Row, others: readonly Row[]) => Row = (winner) => winner,
): Row[] {
  return Array.from(groupRows(rows, deriveId), ([id, group]) => {
    const [winner, ...others] = group.sort(compareRows(id));
    if (winner === undefined) {
      throw new Error("An export row group cannot be empty");
    }
    return { ...merge(winner, others), id };
  });
}

function latest(values: readonly (string | null)[]): string | null {
  let best: string | null = null;
  for (const value of values) {
    if (value !== null && (best === null || value > best)) {
      best = value;
    }
  }
  return best;
}

/**
 * Rewrite an export so its rows carry the ids this app derives for them (see `database.md`,
 * "Clean baseline and identity"). It runs on everything that is imported or restored, so data
 * from any source enters the database in the one form two devices agree on:
 *
 * - an export or backup written before ids were derived (random ids, possibly several rows for
 *   one key, several `user` rows or Favorites collections left behind by old sync merges);
 * - an export this build wrote, which already has these ids and comes out unchanged.
 *
 * Rows that are one row to the app are folded into one, and every reference to a folded or
 * re-keyed row follows it, so nothing that was related before is orphaned by the rewrite.
 */
export function normalizeActorDataExportIdentities(
  tables: ExportTables,
): NormalizedActorDataTables {
  // The oldest `user` row is the account's, as the sync merge that preceded this decided.
  const [user] = [...tables.user].sort(
    (a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id),
  );

  // One Favorites collection. Memberships of the others move to it.
  const favorites = tables.collection.filter((collection) => collection.isFavorites);
  const [canonicalFavorites] = [...favorites].sort(compareRows(FAVORITES_COLLECTION_ID));
  const collectionIdMap = new Map(
    favorites.map((collection) => [collection.id, FAVORITES_COLLECTION_ID]),
  );
  const collection = tables.collection
    .filter((row) => !row.isFavorites || row === canonicalFavorites)
    .map((row) => ({
      ...row,
      id: collectionIdMap.get(row.id) ?? row.id,
      userId: row.userId === null ? null : ACTOR_USER_ID,
    }));
  const collectionMorpheme = rekeyRows(
    tables.collectionMorpheme.map((row) => ({
      ...row,
      collectionId: collectionIdMap.get(row.collectionId) ?? row.collectionId,
    })),
    (row) => deriveCollectionMorphemeId(row),
  );

  // Cards: the overlay row names the card, and its `user_card` and schedule follow it.
  const userCardById = new Map(tables.userCard.map((card) => [card.id, card]));
  const userCardIdMap = new Map<string, string>();
  const fsrsCardIdMap = new Map<string, string>();
  const keptUserCardIds = new Set<string>();
  const foldedUserCardIds = new Set<string>();
  function rekeyOverlayRows<Row extends CrudRow & { userCardId: string }>(
    rows: readonly Row[],
    deriveIds: (row: Row) => ActorCardRowIds,
  ): Row[] {
    return Array.from(
      groupRows(rows, (row) => deriveIds(row).detailId),
      ([detailId, group]) => {
        const [winner] = group.sort(compareRows(detailId));
        if (winner === undefined) {
          throw new Error("An export row group cannot be empty");
        }
        const ids = deriveIds(winner);
        for (const row of group) {
          // Reviews of a folded duplicate card are reviews of this card.
          userCardIdMap.set(row.userCardId, ids.userCardId);
          foldedUserCardIds.add(row.userCardId);
        }
        keptUserCardIds.add(winner.userCardId);
        const schedule = userCardById.get(winner.userCardId)?.fsrsCardId;
        if (schedule !== null && schedule !== undefined) {
          fsrsCardIdMap.set(schedule, ids.fsrsCardId);
        }
        return { ...winner, id: ids.detailId, userCardId: ids.userCardId };
      },
    );
  }
  const userGraphemeMeaningCard = rekeyOverlayRows(tables.userGraphemeMeaningCard, (row) =>
    deriveGraphemeMeaningCardRowIds(row.grapheme),
  );
  const userGraphemePronunciationCard = rekeyOverlayRows(
    tables.userGraphemePronunciationCard,
    (row) => deriveGraphemePronunciationCardRowIds(row),
  );
  const userWordSentenceCard = rekeyOverlayRows(tables.userWordSentenceCard, (row) =>
    deriveWordSentenceCardRowIds(row),
  );
  const userQuizCard = rekeyOverlayRows(tables.userQuizCard, (row) =>
    deriveQuizCardRowIds(row.key),
  );
  const userCard = tables.userCard
    .filter((card) => keptUserCardIds.has(card.id) || !foldedUserCardIds.has(card.id))
    .map((card) => ({
      ...card,
      fsrsCardId:
        card.fsrsCardId === null ? null : (fsrsCardIdMap.get(card.fsrsCardId) ?? card.fsrsCardId),
      id: userCardIdMap.get(card.id) ?? card.id,
    }));
  // A card's schedule takes the card's derived schedule id. A row that already sat under that id
  // without being the card's schedule (a retired one) gives way to it.
  const scheduleIds = new Set(fsrsCardIdMap.values());
  const fsrsCard = tables.fsrsCard
    .filter((row) => fsrsCardIdMap.has(row.id) || !scheduleIds.has(row.id))
    .map((row) => ({ ...row, id: fsrsCardIdMap.get(row.id) ?? row.id }));

  return {
    ...tables,
    collection,
    collectionMorpheme,
    fsrsCard,
    morphemeNote: rekeyRows(tables.morphemeNote, (row) => deriveMorphemeNoteId(row.morpheme)),
    morphemeSearchVisit: rekeyRows(
      tables.morphemeSearchVisit,
      (row) => deriveMorphemeSearchVisitId(row.front),
      (winner, others) => ({
        ...winner,
        searchedAt: latest([winner, ...others].map((row) => row.searchedAt)) ?? winner.searchedAt,
      }),
    ),
    user: user === undefined ? [] : [{ ...user, id: ACTOR_USER_ID }],
    userCard,
    userCardReview: tables.userCardReview.map((review) => ({
      ...review,
      cardId: userCardIdMap.get(review.cardId) ?? review.cardId,
    })),
    userGraphemeMeaningCard,
    userGraphemePronunciationCard,
    userQuizCard,
    userSentence: rekeyRows(tables.userSentence, (row) => deriveUserSentenceId(row.sentence)),
    // A slide row without a key predates the key and cannot be matched to a slide.
    userSlide: rekeyRows(
      tables.userSlide.flatMap((row) => (row.key === null ? [] : [{ ...row, key: row.key }])),
      (row) => deriveUserSlideId(row.key),
      // Completing a slide and favoriting it are independent, so a duplicate keeps both.
      (winner, others) => {
        const live = [winner, ...others].filter((row) => row.deletedAt === winner.deletedAt);
        return {
          ...winner,
          completedAt: latest(live.map((row) => row.completedAt)),
          favoritedAt: latest(live.map((row) => row.favoritedAt)),
        };
      },
    ),
    userWord: rekeyRows(tables.userWord, (row) => deriveUserWordId(row.word)),
    userWordSentenceCard,
  };
}
